import React, { useState } from 'react';
import {
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardTitle,
  IonTextarea,
  IonButton,
  IonIcon,
  IonText,
  IonItem,
  IonLabel,
  IonSelect,
  IonSelectOption,
  IonToggle,
  IonInput,
  IonSpinner,
} from '@ionic/react';
import {
  chevronDownOutline,
  chevronUpOutline,
  shieldCheckmarkOutline,
  listOutline,
  chatbubbleOutline,
  checkmarkCircleOutline,
} from 'ionicons/icons';
import { CorrectiveActionFormData, CAResponseEntry, FileAttachment, AttachmentType } from '@/types/model/inspection-modal';
import { CorrectiveAction } from '@/types/model/inspection';
import AttachmentsSection from '@/components/common/AttachmentsSection';
import { FileResponse } from '@/types/response/InspectionResultResponses';
import { apiService } from '@/services/ApiService';
import lookupDataService from '@/services/data/LookupDataService';
import permissionService from '@/services/PermissionService';
import { Capacitor } from '@capacitor/core';

interface CorrectiveActionCardProps {
  ca?: CorrectiveAction;
  caIndex: number;
  formData: CorrectiveActionFormData;
  globalFiles: FileResponse[];
  onChange: (data: Partial<CorrectiveActionFormData>) => void;
  readOnly?: boolean;
}

const ACCEPTANCE_STATUS_IDS = [2, 3, 4, 5];

const CorrectiveActionCard: React.FC<CorrectiveActionCardProps> = ({
  ca,
  caIndex,
  formData,
  globalFiles,
  onChange,
  readOnly = false,
}) => {
  const [expandedSections, setExpandedSections] = useState<{ attachments: boolean; validation: boolean; log: boolean }>({
    attachments: false,
    validation: false,
    log: false,
  });
  const [editingLogEntry, setEditingLogEntry] = useState<number | null>(null);
  const [loadingResponses, setLoadingResponses] = useState(false);

  const CA_STATUSES = lookupDataService.getCorrectiveActionStatuses();
  const classificationsMap = lookupDataService.getClassifications();
  const classifications = Array.from(classificationsMap?.values() ?? []).filter(c => c.IsActive !== false);

  const fd = formData;
  const isMobilePlatform = Capacitor.getPlatform() !== 'web';

  // ─── Section helpers ─────────────────────────────────────────────────────────

  const toggleSection = (section: keyof typeof expandedSections) =>
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));

  // ─── Date helpers ────────────────────────────────────────────────────────────

  const formatDate = (dateStr?: string | null) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString();
  };

  const toDateInputValue = (dateStr?: string | null) => {
    if (!dateStr) return '';
    return dateStr.substring(0, 10);
  };

  // ─── CA attachment handlers ──────────────────────────────────────────────────

  const removeCAAttachment = (_: number, attachIdx: number) => {
    onChange({ attachments: fd.attachments.filter((__, i) => i !== attachIdx) });
  };

  const handleCAAttachmentFileChange = (_: number, attachIdx: number, file: File) => {
    onChange({
      attachments: fd.attachments.map((a, i) =>
        i === attachIdx ? { ...a, file, fileName: file.name, size: file.size } : a
      ),
    });
  };

  const handleCAAttachmentLinkChange = (_: number, attachIdx: number, link: string) => {
    onChange({
      attachments: fd.attachments.map((a, i) => (i === attachIdx ? { ...a, link } : a)),
    });
  };

  const handleCAExistingFileSelect = (_: number, attachIdx: number, fileId: string) => {
    onChange({
      attachments: fd.attachments.map((a, i) => (i === attachIdx ? { ...a, fileId } : a)),
    });
  };

  const handleCAAttachmentDescriptionChange = (_: number, attachIdx: number, description: string) => {
    onChange({
      attachments: fd.attachments.map((a, i) => (i === attachIdx ? { ...a, description } : a)),
    });
  };

  const handleAddCAAttachment = (type: AttachmentType) => {
    if (type === 'image' && !isMobilePlatform) {
      return;
    }

    onChange({ attachments: [...fd.attachments, { type }] });
  };

  // ─── Response log handlers ───────────────────────────────────────────────────

  const handleLoadResponses = async () => {
    if (fd.responses !== undefined || loadingResponses || !ca?.Id) return;
    setLoadingResponses(true);
    try {
      const data = await apiService.getCorrectiveActionResponses(ca!.Id);
      const entries: CAResponseEntry[] = data.map(r => ({
        id: r.Id,
        type: r.StatusId === 1 ? 'response' : 'acceptance',
        statusId: r.StatusId,
        savedOn: r.Date,
        savedBy: r.Username,
        comments: r.Comments,
        attachments: [],
      }));
      onChange({ responses: entries });
    } catch {
      onChange({ responses: [] });
    } finally {
      setLoadingResponses(false);
    }
  };

  const handleAddLogEntry = (type: 'response' | 'acceptance') => {
    const newEntry: CAResponseEntry = {
      type,
      statusId: type === 'response' ? 1 : 2,
      comments: '',
      attachments: [],
    };
    const current = fd.responses ?? [];
    onChange({ responses: [...current, newEntry] });
    setEditingLogEntry(current.length);
  };

  const updateLogEntry = (entryIndex: number, data: Partial<CAResponseEntry>) => {
    onChange({
      responses: (fd.responses ?? []).map((r, i) => (i === entryIndex ? { ...r, ...data } : r)),
    });
  };

  const handleSaveLogEntry = async (entryIndex: number) => {
    const entry = fd.responses?.[entryIndex];
    if (!entry || !ca?.Id) return;
    if (!entry.id) {
      // TODO: capture returned ID to update entry.id after save
      await apiService.saveCorrectiveActionResponse({
        comments: entry.comments,
        statusId: entry.statusId,
        correctiveActionId: ca.Id,
        attachments: { Add: [], Edit: [], Delete: [] },
      });
    }
    // TODO: update endpoint for existing entries (entry.id defined)
    setEditingLogEntry(null);
  };

  // ─── Response-entry attachment handlers (factory) ────────────────────────────

  const makeResponseAttachmentHandlers = (entryIndex: number) => {
    const update = (updater: (atts: FileAttachment[]) => FileAttachment[]) => {
      const responses = fd.responses ?? [];
      const entry = responses[entryIndex];
      if (!entry) return;
      onChange({
        responses: responses.map((r, i) =>
          i === entryIndex ? { ...r, attachments: updater(r.attachments) } : r
        ),
      });
    };
    return {
      onDelete: (_: number, aIdx: number) => update(atts => atts.filter((__, i) => i !== aIdx)),
      onFileUpdate: (_: number, aIdx: number, file: File) =>
        update(atts => atts.map((a, i) => (i === aIdx ? { ...a, file, fileName: file.name, size: file.size } : a))),
      onLinkUpdate: (_: number, aIdx: number, link: string) =>
        update(atts => atts.map((a, i) => (i === aIdx ? { ...a, link } : a))),
      onExistingFileSelect: (_: number, aIdx: number, fileId: string) =>
        update(atts => atts.map((a, i) => (i === aIdx ? { ...a, fileId } : a))),
      onDescriptionUpdate: (_: number, aIdx: number, description: string) =>
        update(atts => atts.map((a, i) => (i === aIdx ? { ...a, description } : a))),
    };
  };

  const handleAddResponseAttachment = (entryIndex: number, type: AttachmentType) => {
    if (type === 'image' && !isMobilePlatform) {
      return;
    }

    const responses = fd.responses ?? [];
    const entry = responses[entryIndex];
    if (!entry) return;
    onChange({
      responses: responses.map((r, i) =>
        i === entryIndex ? { ...r, attachments: [...r.attachments, { type }] } : r
      ),
    });
  };

  // ─── Style helpers ────────────────────────────────────────────────────────────

  const statusBadgeStyle = (statusId: number): React.CSSProperties => {
    const colors: Record<number, string> = {
      0: 'var(--ion-color-medium)',
      1: 'var(--ion-color-warning)',
      2: 'var(--ion-color-warning)',
      3: 'var(--ion-color-success)',
      4: 'var(--ion-color-success)',
      5: 'var(--ion-color-primary)',
      6: 'var(--ion-color-danger)',
    };
    const color = colors[statusId] ?? 'var(--ion-color-medium)';
    return {
      display: 'inline-block',
      fontSize: '0.75rem',
      fontWeight: '600',
      padding: '2px 8px',
      borderRadius: '12px',
      marginLeft: '8px',
      background: `${color}22`,
      color,
      border: `1px solid ${color}44`,
    };
  };

  const sectionHeaderStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '10px 0',
    cursor: 'pointer',
    borderTop: '1px solid var(--ion-color-light-shade)',
    marginTop: '8px',
    userSelect: 'none',
  };

  // ─── Derived values ───────────────────────────────────────────────────────────

  const effectiveStatusId = ca?.StatusId ?? fd.statusId ?? 0;
  const statusName = CA_STATUSES.find(s => s.Id === effectiveStatusId)?.Name ?? `Status ${effectiveStatusId}`;
  const hasAcceptance = fd.responses?.some(r => r.type === 'acceptance') ?? false;

  // ─── Render ──────────────────────────────────────────────────────────────────

  return (
    <IonCard style={{ marginBottom: '16px', width: '100%', maxWidth: 'none' }}>

      {/* Header */}
      <IonCardHeader style={{ paddingBottom: '4px' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: '4px' }}>
          <div>
            <IonCardTitle style={{ fontSize: '1rem', fontWeight: '600', display: 'inline' }}>
              CA {caIndex + 1}
            </IonCardTitle>
            <span style={statusBadgeStyle(effectiveStatusId)}>{statusName}</span>
          </div>
          <IonText color="medium" style={{ fontSize: '0.8rem' }}>
            Issued {formatDate(ca?.Issued)}&ensp;·&ensp;By {fd.issuedBy || (ca?.IssuedUserId ? `User ${ca.IssuedUserId}` : '—')}
          </IonText>
        </div>
      </IonCardHeader>

      <IonCardContent style={{ paddingTop: '4px' }}>

        {/* Classification */}
        <IonItem lines="inset">
          <IonLabel position="stacked" style={{ marginBottom: '5px' }}>Classification</IonLabel>
          <IonSelect
            value={fd.classificationId}
            placeholder="Select classification"
            onIonChange={e => onChange({ classificationId: e.detail.value })}
            disabled={readOnly}
          >
            {classifications.map(c => (
              <IonSelectOption key={c.Id} value={c.Id}>{c.Name}</IonSelectOption>
            ))}
          </IonSelect>
        </IonItem>

        {/* Description */}
        <IonItem lines="inset" style={{ marginTop: '8px' }}>
          <IonLabel position="stacked" style={{ marginBottom: '5px' }}>Description</IonLabel>
          <IonTextarea
            value={fd.issue}
            placeholder="Describe the issue and required corrective action…"
            rows={3}
            autoGrow
            onIonInput={e => onChange({ issue: e.detail.value! })}
            readonly={readOnly}
            style={{ '--background': '#f8f9fa', '--border-radius': '4px' }}
          />
        </IonItem>

        {/* ── Attachments section ── */}
        <AttachmentsSection
          attachments={fd.attachments}
          onAddAttachment={handleAddCAAttachment}
          onDelete={(index) => removeCAAttachment(0, index)}
          onFileUpdate={(index, file) => handleCAAttachmentFileChange(0, index, file)}
          onLinkUpdate={(index, link) => handleCAAttachmentLinkChange(0, index, link)}
          onExistingFileSelect={(index, fileId) => handleCAExistingFileSelect(0, index, fileId)}
          onDescriptionUpdate={(index, description) => handleCAAttachmentDescriptionChange(0, index, description)}
          existingFiles={globalFiles}
          readOnly={readOnly}
          collapsible={true}
          isExpanded={expandedSections.attachments}
          onExpandChange={(expanded) => {
            if (expanded !== expandedSections.attachments) {
              toggleSection('attachments');
            }
          }}
          existingAttachmentCount={fd.attachments.length === 0 ? ca?.AttachmentCount : 0}
        />

        {/* ── Validation section ── */}
        <div>
          <div style={sectionHeaderStyle} onClick={() => toggleSection('validation')}>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.9rem', fontWeight: '500' }}>
              <IonIcon icon={shieldCheckmarkOutline} />
              Validation
              {fd.isValid !== null && fd.isValid !== undefined && (
                <span style={{ fontSize: '0.75rem', color: fd.isValid ? 'var(--ion-color-success)' : 'var(--ion-color-danger)' }}>
                  ({fd.isValid ? 'Valid' : 'Invalid'})
                </span>
              )}
            </span>
            <IonIcon
              icon={expandedSections.validation ? chevronUpOutline : chevronDownOutline}
              style={{ color: 'var(--ion-color-medium)' }}
            />
          </div>

          {expandedSections.validation && (
            <div style={{ paddingBottom: '8px' }}>
              <IonItem lines="none">
                <IonLabel>Is Valid</IonLabel>
                <IonToggle
                  slot="end"
                  checked={fd.isValid ?? false}
                  onIonChange={e => onChange({ isValid: e.detail.checked })}
                  disabled={readOnly}
                />
              </IonItem>
              <IonItem lines="inset">
                <IonLabel position="stacked">Validated On</IonLabel>
                <IonInput
                  type="date"
                  value={toDateInputValue(fd.validatedOn)}
                  onIonChange={e => onChange({ validatedOn: (e.detail.value as string) || undefined })}
                  readonly={readOnly}
                />
              </IonItem>
              <IonItem lines="inset">
                <IonLabel position="stacked">Validated By</IonLabel>
                <IonInput value={fd.validatedBy ?? ''} readonly />
              </IonItem>
              <IonItem lines="inset">
                <IonLabel position="stacked" style={{ marginBottom: '5px' }}>Comments</IonLabel>
                <IonTextarea
                  value={fd.validationComments ?? ''}
                  rows={2}
                  autoGrow
                  onIonInput={e => onChange({ validationComments: e.detail.value! })}
                  readonly={readOnly}
                  style={{ '--background': '#f8f9fa', '--border-radius': '4px' }}
                />
              </IonItem>
            </div>
          )}
        </div>

        {/* ── Response / Acceptance Log ── */}
        <div>
          <div
            style={sectionHeaderStyle}
            onClick={() => {
              const wasExpanded = expandedSections.log;
              toggleSection('log');
              if (!wasExpanded && ca?.Id) handleLoadResponses();
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.9rem', fontWeight: '500' }}>
              <IonIcon icon={listOutline} />
              Response / Acceptance Log
              {fd.responses !== undefined && (
                <span style={{ fontSize: '0.75rem', color: 'var(--ion-color-medium)' }}>
                  ({fd.responses.length})
                </span>
              )}
            </span>
            <IonIcon
              icon={expandedSections.log ? chevronUpOutline : chevronDownOutline}
              style={{ color: 'var(--ion-color-medium)' }}
            />
          </div>

          {expandedSections.log && (
            <div style={{ paddingBottom: '12px' }}>
              {!ca?.Id ? (
                <IonText color="medium">
                  <p style={{ fontStyle: 'italic', margin: '8px 0' }}>
                    Save this corrective action first to add responses.
                  </p>
                </IonText>
              ) : loadingResponses ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 0' }}>
                  <IonSpinner name="dots" />
                  <IonText color="medium">Loading responses…</IonText>
                </div>
              ) : (
                <>
                  {/* Add buttons — permission-gated independently of readOnly (responses can be added in view mode) */}
                  {(permissionService.canAddResponse(effectiveStatusId) || permissionService.canAddAcceptance(effectiveStatusId)) && (
                    <div style={{ display: 'flex', gap: '8px', marginBottom: '12px', flexWrap: 'wrap' }}>
                      {permissionService.canAddResponse(effectiveStatusId) && (
                        <IonButton
                          fill="outline"
                          size="small"
                          onClick={() => handleAddLogEntry('response')}
                        >
                          <IonIcon icon={chatbubbleOutline} slot="start" />
                          Add Response
                        </IonButton>
                      )}
                      {permissionService.canAddAcceptance(effectiveStatusId) && (
                        <IonButton
                          fill="outline"
                          size="small"
                          disabled={hasAcceptance}
                          onClick={() => handleAddLogEntry('acceptance')}
                        >
                          <IonIcon icon={checkmarkCircleOutline} slot="start" />
                          Add Acceptance
                        </IonButton>
                      )}
                    </div>
                  )}

                  {/* Column headers */}
                  {(fd.responses?.length ?? 0) > 0 && (
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: '130px 130px 1fr 2fr 56px',
                      gap: '8px',
                      fontSize: '0.75rem',
                      fontWeight: '600',
                      color: 'var(--ion-color-medium)',
                      borderBottom: '1px solid var(--ion-color-light-shade)',
                      paddingBottom: '6px',
                      marginBottom: '4px',
                    }}>
                      <div>Last Saved On</div>
                      <div>Last Saved By</div>
                      <div>Action</div>
                      <div>Comments</div>
                      <div style={{ textAlign: 'right' }}>Att #</div>
                    </div>
                  )}

                  {fd.responses?.length === 0 && (
                    <IonText color="medium">
                      <p style={{ fontStyle: 'italic', margin: '8px 0' }}>No entries yet.</p>
                    </IonText>
                  )}

                  {fd.responses?.map((entry, eIdx) => {
                    const isEditing = editingLogEntry === eIdx;
                    const canEdit = permissionService.canEditResponse(
                      entry.statusId,
                      ca?.IssuedUserId ?? 0,
                      fd.responses ?? [],
                      eIdx,
                    );
                    const actionLabel =
                      CA_STATUSES.find(s => s.Id === entry.statusId)?.Name ?? `Status ${entry.statusId}`;

                    if (isEditing && canEdit) {
                      const responseHandlers = makeResponseAttachmentHandlers(eIdx);
                      return (
                        <div
                          key={eIdx}
                          style={{
                            background: 'var(--ion-color-light-tint)',
                            border: '1px solid var(--ion-color-light-shade)',
                            borderRadius: '8px',
                            padding: '12px',
                            marginBottom: '8px',
                          }}
                        >
                          {/* Status — acceptance only */}
                          {entry.type === 'acceptance' && (
                            <IonItem lines="inset" style={{ marginBottom: '4px' }}>
                              <IonLabel position="stacked">Status</IonLabel>
                              <IonSelect
                                value={entry.statusId}
                                onIonChange={e => updateLogEntry(eIdx, { statusId: e.detail.value })}
                              >
                                {ACCEPTANCE_STATUS_IDS.map(id => (
                                  <IonSelectOption key={id} value={id}>
                                    {CA_STATUSES.find(s => s.Id === id)?.Name}
                                  </IonSelectOption>
                                ))}
                              </IonSelect>
                            </IonItem>
                          )}

                          {/* Comments */}
                          <IonItem lines="inset">
                            <IonLabel position="stacked">Comments</IonLabel>
                            <IonTextarea
                              value={entry.comments}
                              rows={3}
                              autoGrow
                              placeholder="Enter comments…"
                              onIonInput={e => updateLogEntry(eIdx, { comments: e.detail.value! })}
                              style={{ '--background': '#fff', '--border-radius': '4px' }}
                            />
                          </IonItem>

                          {/* Attachments Section */}
                          <IonItem>
                            <AttachmentsSection
                              attachments={entry.attachments}
                              onAddAttachment={(type) => handleAddResponseAttachment(eIdx, type)}
                              onDelete={(index) => responseHandlers.onDelete(0, index)}
                              onFileUpdate={(index, file) => responseHandlers.onFileUpdate(0, index, file)}
                              onLinkUpdate={(index, link) => responseHandlers.onLinkUpdate(0, index, link)}
                              onExistingFileSelect={(index, fileId) => responseHandlers.onExistingFileSelect(0, index, fileId)}
                              onDescriptionUpdate={(index, description) => responseHandlers.onDescriptionUpdate(0, index, description)}
                              existingFiles={globalFiles}
                              title="Attachments"
                            />
                          </IonItem>

                          {/* Save / Cancel */}
                          <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                            <IonButton
                              fill="solid"
                              size="small"
                              onClick={() => handleSaveLogEntry(eIdx)}
                            >
                              Save
                            </IonButton>
                            <IonButton
                              fill="outline"
                              size="small"
                              color="medium"
                              onClick={() => setEditingLogEntry(null)}
                            >
                              Cancel
                            </IonButton>
                          </div>
                        </div>
                      );
                    }

                    // Read-only log row
                    return (
                      <div
                        key={eIdx}
                        style={{
                          display: 'grid',
                          gridTemplateColumns: '130px 130px 1fr 2fr 56px',
                          gap: '8px',
                          padding: '8px 4px',
                          borderBottom: '1px solid var(--ion-color-light-shade)',
                          cursor: canEdit ? 'pointer' : 'default',
                          fontSize: '0.85rem',
                          alignItems: 'start',
                          borderRadius: '4px',
                        }}
                        onClick={() => { if (canEdit) setEditingLogEntry(eIdx); }}
                        onMouseEnter={e => { if (canEdit) e.currentTarget.style.background = 'var(--ion-color-light-tint)'; }}
                        onMouseLeave={e => (e.currentTarget.style.background = '')}
                      >
                        <div style={{ color: 'var(--ion-color-medium)', fontSize: '0.8rem' }}>
                          {entry.savedOn ? formatDate(entry.savedOn) : <em>Not saved</em>}
                        </div>
                        <div>{entry.savedBy ?? '—'}</div>
                        <div>
                          <span style={statusBadgeStyle(entry.statusId)}>{actionLabel}</span>
                        </div>
                        <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {entry.comments || <em style={{ color: 'var(--ion-color-medium)' }}>No comments</em>}
                        </div>
                        <div style={{ textAlign: 'right', color: 'var(--ion-color-medium)' }}>
                          {entry.attachments.length}
                        </div>
                      </div>
                    );
                  })}
                </>
              )}
            </div>
          )}
        </div>

      </IonCardContent>
    </IonCard>
  );
};

export default CorrectiveActionCard;
